import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "./lib/api";

export function Card({ className = "", children, ...p }) {
  return (
    <div className={`bg-[#111827]/90 border border-slate-800 rounded-lg shadow-xl shadow-black/40 ${className}`} {...p}>
      {children}
    </div>
  );
}

export function Btn({ variant = "primary", className = "", children, ...p }) {
  const v = {
    primary: "bg-sky-500 hover:bg-sky-600 text-sky-50 shadow-lg shadow-sky-950/40",
    ghost: "bg-slate-800/60 hover:bg-slate-700 text-slate-200 border border-slate-700",
    danger: "bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-950/50",
    subtle: "bg-transparent hover:bg-slate-800 text-slate-300",
  }[variant];
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed [transition:color_.2s,background-color_.2s,border-color_.2s] ${v} ${className}`}
      {...p}
    >
      {children}
    </button>
  );
}

export function Label({ children }) {
  return <label className="block text-xs font-mono uppercase tracking-wider text-slate-400 mb-1.5">{children}</label>;
}

export function Input({ className = "", ...p }) {
  return (
    <input
      className={`w-full bg-slate-950/60 border border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/40 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none [transition:border-color_.2s] ${className}`}
      {...p}
    />
  );
}

export function Select({ className = "", children, ...p }) {
  return (
    <select
      className={`w-full bg-slate-950/60 border border-slate-700 focus:border-sky-500 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none ${className}`}
      {...p}
    >
      {children}
    </select>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading text-slate-100">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Badge({ tone = "slate", children, className = "", ...p }) {
  const t = {
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    sky: "bg-sky-500/10 text-sky-300 border-sky-500/20",
    purple: "bg-violet-500/10 text-violet-300 border-violet-500/20",
    indigo: "bg-indigo-500/10 text-indigo-300 border-indigo-500/20",
  }[tone];
  return <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-mono font-medium border ${t} ${className}`} {...p}>{children}</span>;
}

export function StatusBadge({ status }) {
  const map = { draft: "amber", finalized: "sky", paid: "emerald", partial: "amber", overdue: "rose", cancelled: "rose" };
  const label = status === "draft" ? "PREVIEW" : String(status || "").toUpperCase();
  return <Badge tone={map[status] || "slate"}>{label}</Badge>;
}

export function Modal({ open, onClose, title, children, danger }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-6 sm:pt-10 overflow-y-auto bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`w-full max-w-lg rounded-xl p-6 shadow-2xl ${danger ? "bg-[#180A0A] border border-rose-800" : "bg-[#111827] border border-slate-800"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold font-heading text-slate-100">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Th({ children, className = "" }) {
  return <th className={`sticky top-14 md:top-0 z-10 bg-[#0F172A] shadow-[inset_0_-1px_0_rgba(51,65,85,0.6)] text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500 font-medium ${className}`}>{children}</th>;
}
export function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-sm text-slate-200 ${className}`}>{children}</td>;
}

export function ClientPicker({ selectedLabel, onSelect, testid = "client-picker", endpoint = "/clients", placeholder = "Search client by name / GSTIN…" }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [openList, setOpenList] = useState(false);
  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      api.get(endpoint, { params: { q, limit: 20 } }).then((r) => { if (active) setRows(r.data.items || []); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, endpoint]);
  return (
    <div className="relative">
      <Input data-testid={`${testid}-input`} value={openList ? q : (selectedLabel || q)} placeholder={placeholder}
        onFocus={() => setOpenList(true)} onBlur={() => setTimeout(() => setOpenList(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpenList(true); }} />
      {openList && rows.length > 0 && (
        <div className="absolute z-[60] mt-1 w-full max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl">
          {rows.map((c) => (
            <button key={c.id} type="button" data-testid={`${testid}-opt-${c.id}`}
              className="block w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800 border-b border-slate-800/60 last:border-0"
              onMouseDown={() => { onSelect(c); setOpenList(false); setQ(""); }}>
              {c.name}{c.gstin ? <span className="text-slate-500 font-mono text-xs"> · {c.gstin}</span> : null}{c.country ? <span className="text-slate-500 text-xs"> · {c.country}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Pagination({ skip, limit, total, onPage }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm text-slate-400 border-t border-slate-800/60">
      <span data-testid="pagination-info">{total === 0 ? "0" : `${skip + 1}–${Math.min(skip + limit, total)}`} of {total}</span>
      <div className="flex items-center gap-2">
        <button data-testid="page-prev" disabled={page <= 1} onClick={() => onPage(Math.max(0, skip - limit))} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-40"><ChevronLeft size={16} /></button>
        <span className="font-mono text-xs">{page}/{pages}</span>
        <button data-testid="page-next" disabled={page >= pages} onClick={() => onPage(skip + limit)} className="p-1.5 rounded hover:bg-slate-800 disabled:opacity-40"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}
